if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super()
        this.onChange = this.onChange.bind(this)
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange)
        this.updateOptionAvailability()
        this.syncAllFieldsets()
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange)
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        )
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
      }

      isAvailable(input) {
        return input && !input.disabled && !input.classList.contains('disabled')
      }

      getFieldsets() {
        return Array.from(this.querySelectorAll('fieldset'))
      }

      updateOptionAvailability() {
        const json = this.querySelector('[data-product-variants]')
        if (!json) return

        let variants = []
        try {
          variants = JSON.parse(json.textContent)
        } catch (e) {
          return
        }

        const selected = { 1: null, 2: null, 3: null }

        this.querySelectorAll('input[type="radio"][data-option-position]:checked').forEach((el) => {
          selected[parseInt(el.dataset.optionPosition, 10)] = el.value
        })

        const radios = this.querySelectorAll('input[type="radio"][data-option-position]')

        radios.forEach((el) => {
          const pos = parseInt(el.dataset.optionPosition, 10)
          const val = el.value

          let matchingVariants = []

          if (pos === 1) {
            matchingVariants = variants.filter((v) => v.option1 === val)
          } else if (pos === 2) {
            matchingVariants = variants.filter(
              (v) => v.option1 === selected[1] && v.option2 === val
            )
          } else if (pos === 3) {
            matchingVariants = variants.filter(
              (v) =>
                v.option1 === selected[1] &&
                v.option2 === selected[2] &&
                v.option3 === val
            )
          }

          const exists = matchingVariants.length > 0
          const inStock = matchingVariants.some((v) => v.available)

          el.classList.remove('disabled', 'unavailable')

          if (!exists) {
            el.classList.add('disabled', 'unavailable')
          } else if (!inStock) {
            el.classList.add('disabled')
          }

          const label = this.querySelector(`label[for="${el.id}"]`)
          if (label) {
            label.classList.remove('disabled', 'unavailable')

            if (!exists) {
              label.classList.add('disabled', 'unavailable')
            } else if (!inStock) {
              label.classList.add('disabled')
            }
          }
        })
      }

      updateLegend(fieldset) {
        const checked = fieldset.querySelector('input[type="radio"]:checked')
        const label = fieldset.querySelector('[data-selected-swatch-value], [data-selected-value]')

        if (checked && label) {
          label.textContent = checked.value
        }
      }

      syncFieldset(fieldset) {
        const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
        if (!radios.length) return null

        let checked = radios.find(input => input.checked)
        const firstAvailable = radios.find(input => this.isAvailable(input))

        if (checked && this.isAvailable(checked)) {
          this.updateLegend(fieldset)
          return null
        }

        if (!firstAvailable) {
          this.updateLegend(fieldset)
          return null
        }

        radios.forEach(input => {
          input.checked = false
          input.removeAttribute('checked')
        })

        firstAvailable.checked = true
        firstAvailable.setAttribute('checked', 'checked')
        this.updateLegend(fieldset)

        return firstAvailable
      }

      syncAllFieldsets(fromTarget = null) {
        const fieldsets = this.getFieldsets()
        const changedFieldset = fromTarget ? fromTarget.closest('fieldset') : null
        const changedIndex = changedFieldset ? fieldsets.indexOf(changedFieldset) : -1
        let lastAutoSelected = null

        fieldsets.forEach((fieldset, index) => {
          if (changedIndex > -1 && index <= changedIndex) {
            this.updateLegend(fieldset)
            return
          }

          const changedInput = this.syncFieldset(fieldset)
          if (changedInput) lastAutoSelected = changedInput
        })

        return lastAutoSelected
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options).forEach(option => option.removeAttribute('selected'))
          target.selectedOptions[0].setAttribute('selected', 'selected')

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch')

          if (!selectedDropdownSwatchValue) return

          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue)
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable')
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset')
            selectedDropdownSwatchValue.classList.add('swatch--unavailable')
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          )
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const fieldset = target.closest('.product-form__input')
          if (fieldset) this.updateLegend(fieldset)
        }
      }

      onChange(event) {
        this.updateOptionAvailability()

        const target = this.getInputForEventTarget(event.target)
        const finalTarget =
          target.tagName === 'INPUT' && target.type === 'radio'
            ? this.syncAllFieldsets(target) || target
            : target

        this.updateOptionAvailability()

        this.updateSelectedSwatchValue({ target: finalTarget })

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target: finalTarget,
            selectedOptionValues: this.selectedOptionValues,
          },
        })
      }
    }
  )
}